import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Search, Bell, Menu, X, ChevronDown, LogOut, Settings, Baby, Crown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const links = [
  { to: "/browse", label: "Home" },
  { to: "/movies", label: "Movies" },
  { to: "/series", label: "TV Shows" },
  { to: "/live", label: "Live TV" },
  { to: "/kids", label: "Kids" },
  { to: "/new", label: "New & Popular" },
  { to: "/my-list", label: "My List" },
];

export default function Navbar() {
  const { user, logout, toggleKidsMode } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav" : "bg-gradient-to-b from-black/90 to-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-4 flex items-center gap-8">
        <Link to="/browse" className="flex items-center gap-2 group" data-testid="logo-link">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#E50914] to-[#8B0000] flex items-center justify-center shadow-[0_0_20px_rgba(229,9,20,0.5)] group-hover:scale-110 transition-transform">
            <span className="text-white font-black text-lg" style={{ fontFamily: "Outfit" }}>D</span>
          </div>
          <span className="text-xl font-black tracking-tight text-white hidden sm:inline" style={{ fontFamily: "Outfit" }}>
            Djino<span className="text-[#E50914]">Stream</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1 flex-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+|&/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm font-medium rounded-md transition-all hover:text-white ${
                  isActive ? "text-white" : "text-[#B0B0B0]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3 ml-auto">
          <button
            onClick={() => navigate("/search")}
            className="text-[#B0B0B0] hover:text-white transition p-1.5"
            data-testid="search-icon-btn"
            aria-label="Search"
          >
            <Search size={20} />
          </button>
          <button className="text-[#B0B0B0] hover:text-white transition p-1.5 hidden sm:block" data-testid="notifications-btn" aria-label="Notifications">
            <Bell size={20} />
          </button>

          <div className="relative">
            <button
              onClick={() => setOpenProfile((v) => !v)}
              className="flex items-center gap-1 hover:opacity-90"
              data-testid="profile-dropdown-btn"
            >
              <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#E50914] to-[#7a0a0e] flex items-center justify-center text-white text-sm font-bold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <ChevronDown size={14} className="text-[#B0B0B0]" />
            </button>
            {openProfile && (
              <div className="absolute right-0 mt-2 w-60 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-2xl py-2 animate-fade-in" data-testid="profile-menu">
                <div className="px-4 py-2 border-b border-white/10">
                  <div className="text-sm font-semibold text-white">{user?.name}</div>
                  <div className="text-xs text-[#999]">{user?.email}</div>
                </div>
                <button onClick={() => { toggleKidsMode(true); navigate("/kids"); setOpenProfile(false); }} className="w-full text-left px-4 py-2 text-sm text-[#ccc] hover:bg-white/5 flex items-center gap-2" data-testid="kids-mode-toggle">
                  <Baby size={16} /> Kids Mode
                </button>
                <button onClick={() => { navigate("/subscriptions"); setOpenProfile(false); }} className="w-full text-left px-4 py-2 text-sm text-[#ccc] hover:bg-white/5 flex items-center gap-2" data-testid="subscriptions-link">
                  <Crown size={16} /> Subscription
                </button>
                {user?.role === "admin" && (
                  <button onClick={() => { navigate("/admin"); setOpenProfile(false); }} className="w-full text-left px-4 py-2 text-sm text-[#ccc] hover:bg-white/5 flex items-center gap-2" data-testid="admin-link">
                    <Settings size={16} /> Admin Panel
                  </button>
                )}
                <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-[#ccc] hover:bg-white/5 flex items-center gap-2 border-t border-white/5 mt-1" data-testid="logout-btn">
                  <LogOut size={16} /> Sign Out
                </button>
              </div>
            )}
          </div>

          <button onClick={() => setOpenMenu((v) => !v)} className="lg:hidden text-white p-1.5" data-testid="mobile-menu-btn" aria-label="Menu">
            {openMenu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {openMenu && (
        <div className="lg:hidden glass-nav border-t border-white/5 px-6 py-4 animate-fade-in" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpenMenu(false)}
                className={({ isActive }) =>
                  `px-3 py-2.5 text-sm rounded-md ${isActive ? "bg-white/10 text-white" : "text-[#ccc] hover:bg-white/5"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
